import {
  collectWinterDoppelSeriesAssignments,
  resolveWinterDoppelPlayerIdentity,
} from "./seriesConflicts.js";
import { loadWinterDoppelPlayersForSeriesIds } from "./players.js";
import {
  loadWinterDoppelSessionsForSeriesIds,
  loadWinterDoppelSessionPlayers,
} from "./sessions.js";
import {
  generateWinterDoppelGroupDates,
  loadWinterDoppelPlayerGroupsForSeriesIds,
  withWinterDoppelAlternatingGroupPhases,
} from "./groups.js";
import { getSupabaseClient } from "../supabase/index.js";
import { loadSessions, createSession } from "../trainerstunden/sessions.js";
import {
  attachSessionExternalCourtBookings,
  parseSessionCourtNoFromLabel,
} from "../trainerstunden/session-external-court.js";
import { loadTrainers } from "../trainerstunden/trainers.js";
import { isGroupingPlaceholderTrainer } from "../trainerstunden/free-training-trainer.js";
import { authEmailsEquivalent } from "../trainerstunden/normalize-email-for-auth-match.js";
import { loadClubPeopleByIds } from "../masterdata/people.js";
import { normalizeWinterDoppelPlayerName } from "./playerDisplay.js";
import { generateId, analyzePersonNameInput } from "@tennis-matches/core/utils";
import { createClubPlayerContactFromParsedName } from "../players/index.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __date(v) {
  return String(v || "").slice(0, 10);
}

/** Schlanke Spalten für Hub-/Occupancy-Desk-Termine. */
const OCCUPANCY_DESK_SESSION_SELECT =
  "id,date,start_time,duration_min,title,location,trainer_id,planned_ids,present_ids,declined_ids,attendance_confirmed,deleted_at";

function isDeskSessionSoftDeleted(row) {
  const v = row?.deleted_at;
  return v != null && String(v).trim() !== "";
}

/** @deprecated Prefer parseSessionCourtNoFromLabel from trainerstunden. */
export function parseOccupancyCourtNoFromLabel(raw) {
  return parseSessionCourtNoFromLabel(raw);
}

async function loadOccupancyDeskSessions({ dateFrom = "" } = {}) {
  const from = __date(dateFrom);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) {
    return loadSessions();
  }
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const { data, error } = await supabase
      .from("trainerstunden_sessions")
      .select(OCCUPANCY_DESK_SESSION_SELECT)
      .gte("date", from)
      .order("date", { ascending: true })
      .order("start_time", { ascending: true });
    if (error) {
      console.warn("[loadOccupancyDeskSessions] Error:", error);
      return { ok: false, error };
    }
    const rows = (data || []).filter((r) => !isDeskSessionSoftDeleted(r));
    return { ok: true, data: rows };
  } catch (e) {
    console.error("[loadOccupancyDeskSessions] Exception:", e);
    return { ok: false, error: e };
  }
}

function clubPersonLabel(person) {
  const name =
    `${__asStr(person?.first_name)} ${__asStr(person?.last_name)}`.trim();
  return name || __asStr(person?.display_name) || __asStr(person?.id);
}

function trainerDisplayName(trainer) {
  return (
    `${__asStr(trainer?.first_name)} ${__asStr(trainer?.last_name)}`.trim() ||
    __asStr(trainer?.display_name)
  );
}

export function occupancyPersonKeys(person) {
  return [
    ...new Set(
      [person?.key, ...(person?.keys || [])].map(__asStr).filter(Boolean),
    ),
  ];
}

function occupancyDeskMatchNeedles(selected, people) {
  const clubIds = new Set();
  const trainerIds = new Set();
  const names = new Set();
  const addPerson = (person) => {
    if (person?.clubPersonId) clubIds.add(__asStr(person.clubPersonId));
    for (const key of occupancyPersonKeys(person)) {
      if (key.startsWith("club:")) clubIds.add(key.slice(5));
      if (key.startsWith("trainer:")) trainerIds.add(key.slice(8));
    }
    const label = normalizeWinterDoppelPlayerName(person?.label);
    if (label) names.add(label);
    const firstLast = normalizeWinterDoppelPlayerName(
      `${person?.firstName || ""} ${person?.lastName || ""}`,
    );
    if (firstLast) names.add(firstLast);
    for (const nick of person?.nicknames || []) {
      const n = normalizeWinterDoppelPlayerName(nick);
      if (n) names.add(n);
    }
  };
  for (const key of selected) {
    if (key.startsWith("club:")) clubIds.add(key.slice(5));
    if (key.startsWith("trainer:")) trainerIds.add(key.slice(8));
  }
  for (const person of people || []) {
    if (!occupancyPersonKeys(person).some((key) => selected.has(key))) continue;
    addPerson(person);
  }
  return { clubIds, trainerIds, names };
}

export function occupancyPersonSearchText(person) {
  return [
    person?.label,
    person?.firstName,
    person?.lastName,
    ...(person?.nicknames || []),
    person?.kind === "trainer" || person?.kind === "both" ? "trainer" : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function mergeOccupancyKind(a, b) {
  if (a === b) return a;
  if (a === "both" || b === "both") return "both";
  if (
    (a === "trainer" && b === "player") ||
    (a === "player" && b === "trainer")
  ) {
    return "both";
  }
  return a || b || "player";
}

export function occupancyNicknameLinkPair(people = []) {
  if (!Array.isArray(people) || people.length !== 2) return null;
  const [a, b] = people;
  const score = (person) =>
    (person?.kind === "trainer" || person?.kind === "both" ? 20 : 0) +
    __asStr(person?.label).length;
  if (score(a) >= score(b)) return { canonical: a, alias: b };
  return { canonical: b, alias: a };
}

export function applyOccupancyNicknames(people = [], nicknames = []) {
  const list = (Array.isArray(people) ? people : []).map((person) => ({
    ...person,
    keys: new Set(occupancyPersonKeys(person)),
    nicknames: [...(person.nicknames || [])],
  }));

  const byAlias = new Map();
  const reindex = () => {
    byAlias.clear();
    for (const person of list) {
      for (const key of person.keys) byAlias.set(key, person);
    }
  };
  reindex();

  const findByNickname = (nick) => {
    const norm = normalizeWinterDoppelPlayerName(nick);
    if (!norm) return null;
    return (
      list.find(
        (person) => normalizeWinterDoppelPlayerName(person.label) === norm,
      ) ||
      byAlias.get(`name:${norm}`) ||
      null
    );
  };

  for (const row of nicknames || []) {
    const nick = __asStr(row.nickname);
    const targetKey = __asStr(row.person_key || row.personKey);
    if (!nick || !targetKey) continue;
    const canonical = byAlias.get(targetKey);
    if (!canonical) continue;
    const nickNorm = normalizeWinterDoppelPlayerName(nick);
    if (
      !canonical.nicknames.some(
        (existing) => normalizeWinterDoppelPlayerName(existing) === nickNorm,
      )
    ) {
      canonical.nicknames.push(nick);
    }
    const other = findByNickname(nick);
    if (!other || other === canonical) continue;
    for (const key of other.keys) {
      canonical.keys.add(key);
      byAlias.set(key, canonical);
    }
    canonical.kind = mergeOccupancyKind(canonical.kind, other.kind);
    if (!canonical.clubPersonId && other.clubPersonId) {
      canonical.clubPersonId = other.clubPersonId;
    }
    const idx = list.indexOf(other);
    if (idx >= 0) list.splice(idx, 1);
  }

  return list
    .map((person) => ({
      ...person,
      keys: [...person.keys],
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "de"));
}

export function filterOccupancyPeopleByQuery(
  people = [],
  query = "",
  { excludeKeys = [], limit = 8 } = {},
) {
  const tokens = String(query || "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!tokens.length) return [];
  const exclude = new Set((excludeKeys || []).map(__asStr).filter(Boolean));
  const cap = Number(limit) > 0 ? Number(limit) : 8;
  const out = [];
  for (const person of people || []) {
    if (!person?.key || exclude.has(person.key)) continue;
    const hay = occupancyPersonSearchText(person);
    if (!tokens.every((token) => hay.includes(token))) continue;
    out.push(person);
    if (out.length >= cap) break;
  }
  return out;
}

function selectedAliasSet(selectedKeys, people) {
  const selected = new Set((selectedKeys || []).map(__asStr).filter(Boolean));
  if (!people?.length) return selected;
  const aliases = new Set(selected);
  for (const person of people) {
    const keys = occupancyPersonKeys(person);
    if (!keys.some((key) => selected.has(key))) continue;
    for (const key of keys) aliases.add(key);
  }
  return aliases;
}

function primaryKeyForAlias(alias, people) {
  const needle = __asStr(alias);
  for (const person of people || []) {
    if (occupancyPersonKeys(person).includes(needle)) return person.key;
  }
  return needle;
}

function rowIdentityKeys(row) {
  if (Array.isArray(row?.identityKeys) && row.identityKeys.length) {
    return row.identityKeys.map(__asStr).filter(Boolean);
  }
  const key = __asStr(row?.identityKey);
  return key ? [key] : [];
}

export function catalogWinterDoppelOccupancyPeople(
  seriesContexts = [],
  trainersOrOpts = [],
) {
  /** @type {Map<string, { key: string, keys: Set<string>, label: string, kind: string, clubPersonId: string|null, firstName: string, lastName: string }>} */
  const byKey = new Map();
  const trainers = Array.isArray(trainersOrOpts)
    ? trainersOrOpts
    : trainersOrOpts?.trainers || [];
  const nicknames = Array.isArray(trainersOrOpts)
    ? []
    : trainersOrOpts?.nicknames || [];

  const remember = (person) => {
    byKey.set(person.key, person);
  };

  for (const ctx of seriesContexts || []) {
    for (const player of ctx.players || []) {
      if (!player?.id) continue;
      const identity = resolveWinterDoppelPlayerIdentity(player);
      if (!identity.key) continue;
      const clubPersonId = __asStr(player.club_person_id) || null;
      const prev = byKey.get(identity.key);
      if (!prev) {
        remember({
          key: identity.key,
          keys: new Set([identity.key]),
          label: identity.label,
          kind: "player",
          clubPersonId,
          firstName: "",
          lastName: "",
          nicknames: [],
        });
        continue;
      }
      prev.keys.add(identity.key);
      if (!prev.clubPersonId && clubPersonId) prev.clubPersonId = clubPersonId;
      if (identity.label.length > prev.label.length) {
        prev.label = identity.label;
      }
    }
  }

  for (const trainer of trainers || []) {
    if (!trainer?.id || isGroupingPlaceholderTrainer(trainer)) continue;
    const trainerId = __asStr(trainer.id);
    if (!trainerId) continue;
    const label = trainerDisplayName(trainer);
    if (!label) continue;
    const trainerKey = `trainer:${trainerId}`;
    const linked = __asStr(trainer.linked_player_person_id);
    const clubKey = linked ? `club:${linked}` : "";
    const firstName = __asStr(trainer.first_name);
    const lastName = __asStr(trainer.last_name);
    const existing =
      (clubKey &&
        [...byKey.values()].find(
          (person) =>
            person.key === clubKey ||
            person.keys.has(clubKey) ||
            person.clubPersonId === linked,
        )) ||
      null;
    if (existing) {
      existing.keys.add(trainerKey);
      if (clubKey) existing.keys.add(clubKey);
      existing.kind = existing.kind === "player" ? "both" : existing.kind;
      existing.firstName = existing.firstName || firstName;
      existing.lastName = existing.lastName || lastName;
      if (label.length > existing.label.length) existing.label = label;
      continue;
    }
    remember({
      key: trainerKey,
      keys: new Set([trainerKey, clubKey].filter(Boolean)),
      label,
      kind: "trainer",
      clubPersonId: linked || null,
      firstName,
      lastName,
      nicknames: [],
    });
  }

  return applyOccupancyNicknames(
    [...byKey.values()].map((person) => ({
      ...person,
      keys: [...person.keys],
      nicknames: [...(person.nicknames || [])],
    })),
    nicknames,
  );
}

export function occupancyViewerFromAuth(user, trainers = []) {
  const email = __asStr(user?.email).toLowerCase();
  const trainer =
    email &&
    (trainers || []).find((row) =>
      authEmailsEquivalent(row?.login_email, email),
    );
  const meta =
    user?.user_metadata && typeof user.user_metadata === "object"
      ? user.user_metadata
      : {};
  const fromMeta =
    `${__asStr(meta.first_name)} ${__asStr(meta.last_name)}`.trim();
  const fullName =
    fromMeta ||
    __asStr(meta.full_name || meta.name) ||
    trainerDisplayName(trainer);
  return {
    trainerId: __asStr(trainer?.id),
    clubPersonId: __asStr(trainer?.linked_player_person_id),
    fullName,
    email,
  };
}

/**
 * Stellt sicher, dass der eingeloggte Trainer im Personen-Katalog steht
 * (auch wenn er in keiner Serie vorkommt).
 * @param {object[]} people
 * @param {object} viewer from occupancyViewerFromAuth
 * @param {object[]} [trainers]
 * @returns {object[]}
 */
export function ensureOccupancyViewerInPeople(
  people = [],
  viewer = {},
  trainers = [],
) {
  const list = Array.isArray(people) ? [...people] : [];
  const trainerId = __asStr(viewer?.trainerId);
  if (!trainerId) return list;
  const trainerKey = `trainer:${trainerId}`;
  if (list.some((person) => occupancyPersonKeys(person).includes(trainerKey))) {
    return list;
  }
  const trainer =
    (Array.isArray(trainers) ? trainers : []).find(
      (row) => __asStr(row?.id) === trainerId,
    ) || null;
  const label =
    trainerDisplayName(trainer) || __asStr(viewer?.fullName) || trainerKey;
  if (!label) return list;
  const linked = __asStr(
    trainer?.linked_player_person_id || viewer?.clubPersonId,
  );
  const clubKey = linked ? `club:${linked}` : "";
  list.push({
    key: trainerKey,
    keys: [trainerKey, clubKey].filter(Boolean),
    label,
    kind: "trainer",
    clubPersonId: linked || null,
    firstName: __asStr(trainer?.first_name),
    lastName: __asStr(trainer?.last_name),
    nicknames: [],
  });
  return list;
}

export function matchOccupancyPersonForViewer(people = [], viewer = {}) {
  const trainerId = __asStr(viewer?.trainerId);
  const clubPersonId = __asStr(viewer?.clubPersonId);
  const fullName = __asStr(viewer?.fullName);
  if (trainerId) {
    const key = `trainer:${trainerId}`;
    const hit = people.find((person) =>
      occupancyPersonKeys(person).includes(key),
    );
    if (hit) return hit;
  }
  if (clubPersonId) {
    const key = `club:${clubPersonId}`;
    const hit = people.find(
      (person) =>
        occupancyPersonKeys(person).includes(key) ||
        __asStr(person?.clubPersonId) === clubPersonId,
    );
    if (hit) return hit;
  }
  if (!fullName) return null;
  const hits = filterOccupancyPeopleByQuery(people, fullName, { limit: 20 });
  if (!hits.length) return null;
  const norm = normalizeWinterDoppelPlayerName(fullName);
  const exact = hits.filter(
    (person) => normalizeWinterDoppelPlayerName(person.label) === norm,
  );
  const preferTrainer = (list) => {
    const trainers = list.filter(
      (person) => person.kind === "trainer" || person.kind === "both",
    );
    if (trainers.length === 1) return trainers[0];
    if (list.length === 1) return list[0];
    return null;
  };
  return preferTrainer(exact) || preferTrainer(hits);
}

export function pickUpcomingOccupancyItems(
  days = [],
  { limit = 10, untilDate = "" } = {},
) {
  const cap = Number(limit) > 0 ? Number(limit) : 10;
  const until = __date(untilDate);
  const items = [];
  for (const day of days || []) {
    const date = __date(day?.date);
    if (until && date && date > until) continue;
    for (const item of day?.items || []) {
      items.push({ ...item, date });
      if (items.length >= cap) return items;
    }
  }
  return items;
}

function durationMinFromTimes(start, end) {
  const s = String(start || "").slice(0, 5);
  const e = String(end || "").slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(s) || !/^\d{2}:\d{2}$/.test(e)) return 0;
  const [sh, sm] = s.split(":").map(Number);
  const [eh, em] = e.split(":").map(Number);
  const mins = eh * 60 + em - (sh * 60 + sm);
  return mins > 0 ? mins : 0;
}

function addMinutesToHm(start, mins) {
  const s = String(start || "").slice(0, 5);
  const n = Number(mins);
  if (!/^\d{2}:\d{2}$/.test(s) || !Number.isFinite(n) || n <= 0) return "";
  const [h, m] = s.split(":").map(Number);
  const total = h * 60 + m + Math.round(n);
  const eh = Math.floor(total / 60);
  const em = ((total % 60) + 60) % 60;
  if (eh < 0 || eh >= 24) return "";
  return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
}

function companionsForSession(allRows, sessionId, identityKey) {
  const names = [];
  const seen = new Set();
  for (const row of allRows) {
    if (row.sessionId !== sessionId) continue;
    if (row.identityKey === identityKey) continue;
    const name = row.playerName;
    if (!name || seen.has(row.identityKey)) continue;
    seen.add(row.identityKey);
    names.push({
      name,
      role: row.role,
      rsvp: __asStr(row.rsvp) || "pending",
    });
  }
  return names;
}

function declinedCompanionsFromContexts(
  seriesContexts,
  sessionId,
  identityKey,
) {
  const sid = __asStr(sessionId);
  if (!sid) return [];
  const out = [];
  const seen = new Set();
  for (const ctx of seriesContexts || []) {
    const playerById = new Map(
      (ctx.players || []).filter((p) => p?.id).map((p) => [__asStr(p.id), p]),
    );
    for (const sp of ctx.sessionPlayers || []) {
      if (__asStr(sp.session_id) !== sid) continue;
      if (__asStr(sp.rsvp) !== "no") continue;
      const player = playerById.get(__asStr(sp.player_id));
      if (!player) continue;
      const identity = resolveWinterDoppelPlayerIdentity(player);
      if (identity.key === identityKey || seen.has(identity.key)) continue;
      if (!identity.label) continue;
      seen.add(identity.key);
      out.push({
        name: identity.label,
        role: sp.role === "reserve" ? "reserve" : "starter",
        rsvp: "no",
      });
    }
  }
  return out;
}

function mergeSessionCompanions(
  allRows,
  sessionId,
  identityKey,
  seriesContexts,
) {
  return [
    ...companionsForSession(allRows, sessionId, identityKey),
    ...declinedCompanionsFromContexts(seriesContexts, sessionId, identityKey),
  ];
}

function deskCompanionEntries(ids, clubPeopleById, declinedIds) {
  const declined = new Set((declinedIds || []).map(__asStr).filter(Boolean));
  return (ids || []).map((id) => {
    const pid = __asStr(id);
    return {
      name: clubPersonLabel(clubPeopleById.get(pid)) || pid,
      role: "starter",
      rsvp: declined.has(pid) ? "no" : "pending",
    };
  });
}

function deskPersonIds(session) {
  const planned = Array.isArray(session?.planned_ids)
    ? session.planned_ids
    : [];
  const present = Array.isArray(session?.present_ids)
    ? session.present_ids
    : [];
  return [...new Set([...planned, ...present].map(__asStr).filter(Boolean))];
}

function trainerAssignmentsForContext(ctx, trainersById) {
  const groups = Array.isArray(ctx?.groups) ? ctx.groups : [];
  if (!groups.length) return [];
  const seasonEnd = __asStr(ctx.seasonEnd);
  const phased = withWinterDoppelAlternatingGroupPhases(
    groups.map((group) => ({
      ...group,
      schedule_end:
        group.schedule_end || group.scheduleEnd || seasonEnd || null,
      season_end: seasonEnd || group.season_end || null,
    })),
  );
  /** @type {Map<string, object[]>} */
  const groupsByDate = new Map();
  for (const group of phased) {
    const trainerId = __asStr(group.trainer_id);
    if (!trainerId) continue;
    const dates = generateWinterDoppelGroupDates({
      ...group,
      exclude_holidays: false,
    });
    for (const raw of dates) {
      const date = __date(raw);
      if (!date) continue;
      const list = groupsByDate.get(date) || [];
      list.push(group);
      groupsByDate.set(date, list);
    }
  }
  const out = [];
  for (const session of ctx.sessions || []) {
    if (session?.isHoliday || session?.status === "holiday") continue;
    if (__asStr(session?.status) === "cancelled") continue;
    const date = __date(session.session_date || session.date);
    if (!date) continue;
    const sessionId = __asStr(session.id);
    for (const group of groupsByDate.get(date) || []) {
      const trainerId = __asStr(group.trainer_id);
      const trainer = trainersById.get(trainerId);
      const label = trainerDisplayName(trainer) || trainerId;
      const identityKeys = [`trainer:${trainerId}`];
      const linked = __asStr(trainer?.linked_player_person_id);
      if (linked) identityKeys.push(`club:${linked}`);
      out.push({
        seriesId: __asStr(ctx.seriesId),
        seriesName: __asStr(ctx.seriesName) || __asStr(ctx.seriesId),
        seriesMode: __asStr(ctx.seriesMode) || "training",
        startTime: String(
          group.start_time || ctx.startTime || session.start_time || "",
        ).slice(0, 5),
        endTime: String(group.end_time || ctx.endTime || "").slice(0, 5),
        sessionId,
        isHoliday: false,
        locationLabel:
          __asStr(ctx.locationLabel) || __asStr(ctx.locationId) || "",
        date,
        playerName: label,
        identityKey: identityKeys[0],
        identityKeys,
        role: "trainer",
      });
    }
  }
  return out;
}

/**
 * @param {{
 *   seriesContexts?: object[],
 *   selectedKeys?: string[],
 *   people?: object[],
 *   trainers?: object[],
 *   deskSessions?: object[],
 *   deskLinkIds?: Set<string>|string[],
 *   clubPeopleById?: Map<string, object>,
 *   fromDate?: string,
 * }} input
 */
export function buildWinterDoppelPersonOccupancy({
  seriesContexts = [],
  selectedKeys = [],
  people = [],
  trainers = [],
  deskSessions = [],
  deskLinkIds = [],
  clubPeopleById = new Map(),
  fromDate = "",
} = {}) {
  const selected = selectedAliasSet(selectedKeys, people);
  const from = __date(fromDate);
  const linked =
    deskLinkIds instanceof Set
      ? deskLinkIds
      : new Set((deskLinkIds || []).map(__asStr).filter(Boolean));
  const trainersById = new Map(
    (trainers || []).filter((tr) => tr?.id).map((tr) => [__asStr(tr.id), tr]),
  );

  if (!selected.size) {
    return { days: [], togetherDates: [] };
  }

  const playerRows = (seriesContexts || [])
    .flatMap((ctx) => collectWinterDoppelSeriesAssignments(ctx))
    .filter((row) => !row.isHoliday);
  const trainerRows = (seriesContexts || [])
    .flatMap((ctx) => trainerAssignmentsForContext(ctx, trainersById))
    .filter((row) => !row.isHoliday);
  const allRows = [...playerRows, ...trainerRows];

  const regel = allRows.filter((row) => {
    if (!rowIdentityKeys(row).some((key) => selected.has(key))) return false;
    if (from && row.date < from) return false;
    return true;
  });

  /** @type {Map<string, object>} */
  const byDate = new Map();

  const ensureDay = (date) => {
    let day = byDate.get(date);
    if (!day) {
      day = {
        date,
        selectedKeys: new Set(),
        selectedPeople: new Set(),
        regeltermine: [],
        einzeltermine: [],
      };
      byDate.set(date, day);
    }
    return day;
  };

  const markPerson = (day, identityKey) => {
    day.selectedKeys.add(identityKey);
    day.selectedPeople.add(primaryKeyForAlias(identityKey, people));
  };

  for (const row of regel) {
    const day = ensureDay(row.date);
    markPerson(day, row.identityKey);
    day.regeltermine.push({
      source: "regeltermin",
      date: row.date,
      seriesId: row.seriesId,
      seriesName: row.seriesName,
      seriesMode: row.seriesMode || "free",
      startTime: row.startTime || "",
      endTime: String(row.endTime || "").slice(0, 5),
      sessionId: row.sessionId || "",
      locationLabel: row.locationLabel || "",
      durationMin:
        durationMinFromTimes(row.startTime, row.endTime) || undefined,
      personKey: primaryKeyForAlias(row.identityKey, people),
      personName: row.playerName,
      role: row.role,
      waitlistRank: row.waitlistRank || null,
      companions: mergeSessionCompanions(
        allRows,
        row.sessionId,
        row.identityKey,
        seriesContexts,
      ),
      rsvp: __asStr(row.rsvp) || "",
      courtNo: Number(row.courtNo) > 0 ? Number(row.courtNo) : null,
    });
  }

  const needles = occupancyDeskMatchNeedles(selected, people);

  for (const session of deskSessions || []) {
    const deskId = __asStr(session?.id);
    if (!deskId || linked.has(deskId)) continue;
    if (__asStr(session?.deleted_at)) continue;
    const date = __date(session.date);
    if (!date || (from && date < from)) continue;
    const ids = deskPersonIds(session);
    const hitIds = ids.filter((id) => {
      if (needles.clubIds.has(id) || selected.has(`club:${id}`)) return true;
      const label = clubPersonLabel(clubPeopleById.get(id));
      return Boolean(
        label && needles.names.has(normalizeWinterDoppelPlayerName(label)),
      );
    });
    const trainerId = __asStr(session.trainer_id);
    const trainerKey = trainerId ? `trainer:${trainerId}` : "";
    const trainerHit = Boolean(
      trainerKey &&
      (selected.has(trainerKey) || needles.trainerIds.has(trainerId)),
    );
    if (!hitIds.length && !trainerHit) continue;
    const day = ensureDay(date);
    const declinedIds = Array.isArray(session?.declined_ids)
      ? session.declined_ids
      : [];
    const companionIds = ids.filter((id) => !hitIds.includes(id));
    const companions = deskCompanionEntries(
      companionIds,
      clubPeopleById,
      declinedIds,
    );
    const startTime = String(session.start_time || "").slice(0, 5);
    const durationMin =
      Number(session.duration_min) > 0
        ? Number(session.duration_min)
        : undefined;
    const endTime =
      String(session.end_time || "").slice(0, 5) ||
      addMinutesToHm(startTime, durationMin);
    const locationLabel = __asStr(session.location);
    const attendanceConfirmed = Boolean(session.attendance_confirmed);
    const courtLabel =
      __asStr(session.courtLabel) || __asStr(session.external_court_name);
    const courtNoRaw = Number(session.courtNo);
    const courtNo =
      Number.isFinite(courtNoRaw) && courtNoRaw > 0
        ? courtNoRaw
        : parseSessionCourtNoFromLabel(courtLabel);
    const deskBase = {
      source: "einzeltermin",
      date,
      seriesId: "",
      seriesName: __asStr(session.title) || deskId,
      seriesMode: "einzel",
      startTime,
      endTime,
      sessionId: deskId,
      durationMin,
      locationLabel,
      attendanceConfirmed,
      courtLabel: courtLabel || "",
      courtNo: courtNo || null,
      courtBooked: Boolean(session.courtBooked) || Boolean(courtLabel),
    };
    if (trainerHit) {
      const trainer = trainersById.get(trainerId);
      markPerson(day, trainerKey);
      day.einzeltermine.push({
        ...deskBase,
        personKey: primaryKeyForAlias(trainerKey, people),
        personName: trainerDisplayName(trainer) || trainerId,
        role: "trainer",
        rsvp: "",
        companions: deskCompanionEntries(ids, clubPeopleById, declinedIds),
      });
    }
    for (const id of hitIds) {
      const key = `club:${id}`;
      if (
        trainerHit &&
        primaryKeyForAlias(key, people) ===
          primaryKeyForAlias(trainerKey, people)
      ) {
        continue;
      }
      markPerson(day, key);
      const declined = declinedIds.map(__asStr).includes(__asStr(id));
      day.einzeltermine.push({
        ...deskBase,
        personKey: primaryKeyForAlias(key, people),
        personName: clubPersonLabel(clubPeopleById.get(id)) || id,
        role: "starter",
        rsvp: declined ? "no" : "pending",
        companions,
      });
    }
  }

  const days = [...byDate.values()]
    .map((day) => {
      const selectedOnDay = [...day.selectedKeys];
      return {
        date: day.date,
        together: day.selectedPeople.size >= 2,
        selectedKeys: selectedOnDay,
        items: [...day.regeltermine, ...day.einzeltermine].sort((a, b) =>
          `${a.startTime}|${a.seriesName}|${a.personName}`.localeCompare(
            `${b.startTime}|${b.seriesName}|${b.personName}`,
          ),
        ),
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  return {
    days,
    togetherDates: days.filter((d) => d.together).map((d) => d.date),
  };
}

export async function loadWinterDoppelOccupancySeriesContexts(
  seriesList = [],
  opts = {},
) {
  const list = (Array.isArray(seriesList) ? seriesList : []).filter(
    (s) => s?.id,
  );
  if (!list.length) return [];
  const fromDate = __date(opts.fromDate);
  const toDate = __date(opts.toDate);
  const seriesIds = list.map((s) => __asStr(s.id));

  const [players, groups, sessions] = await Promise.all([
    loadWinterDoppelPlayersForSeriesIds(seriesIds),
    loadWinterDoppelPlayerGroupsForSeriesIds(seriesIds).catch(() => []),
    loadWinterDoppelSessionsForSeriesIds(seriesIds, {
      fromDate: /^\d{4}-\d{2}-\d{2}$/.test(fromDate) ? fromDate : "",
      toDate: /^\d{4}-\d{2}-\d{2}$/.test(toDate) ? toDate : "",
    }),
  ]);

  const sessionPlayers = await loadWinterDoppelSessionPlayers(
    (sessions || []).map((x) => x.id),
  );

  const playersBySeries = new Map();
  for (const p of players || []) {
    const sid = __asStr(p.series_id);
    if (!sid) continue;
    const arr = playersBySeries.get(sid) || [];
    arr.push(p);
    playersBySeries.set(sid, arr);
  }
  const groupsBySeries = new Map();
  for (const g of groups || []) {
    const sid = __asStr(g.series_id);
    if (!sid) continue;
    const arr = groupsBySeries.get(sid) || [];
    arr.push(g);
    groupsBySeries.set(sid, arr);
  }
  const sessionsBySeries = new Map();
  const sessionToSeries = new Map();
  for (const s of sessions || []) {
    const sid = __asStr(s.series_id);
    const sessId = __asStr(s.id);
    if (!sid || !sessId) continue;
    const arr = sessionsBySeries.get(sid) || [];
    arr.push(s);
    sessionsBySeries.set(sid, arr);
    sessionToSeries.set(sessId, sid);
  }
  const spBySeries = new Map();
  for (const sp of sessionPlayers || []) {
    const sid = sessionToSeries.get(__asStr(sp.session_id));
    if (!sid) continue;
    const arr = spBySeries.get(sid) || [];
    arr.push(sp);
    spBySeries.set(sid, arr);
  }

  return list.map((s) => {
    const sid = __asStr(s.id);
    return {
      seriesId: sid,
      seriesName: s.name,
      seriesMode: s.series_mode || s.seriesMode || "free",
      locationId: s.location_id,
      locationLabel: s.location_id || s.name,
      startTime: s.start_time,
      endTime: s.end_time,
      seasonEnd: s.season_end || s.seasonEnd || null,
      players: playersBySeries.get(sid) || [],
      groups: groupsBySeries.get(sid) || [],
      sessions: sessionsBySeries.get(sid) || [],
      sessionPlayers: spBySeries.get(sid) || [],
    };
  });
}

export async function loadWinterDoppelOccupancyDeskExtras(opts = {}) {
  const dateFrom = __date(opts.dateFrom || opts.fromDate);
  const [sessionsRes, trainersRes] = await Promise.all([
    loadOccupancyDeskSessions({ dateFrom }),
    loadTrainers(),
  ]);
  const deskSessionsRaw = sessionsRes?.ok ? sessionsRes.data || [] : [];
  const deskSessions =
    await attachSessionExternalCourtBookings(deskSessionsRaw);
  const trainers = trainersRes?.ok ? trainersRes.data || [] : [];
  let deskLinkIds = [];
  try {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase
      .from("winterdoppel_desk_sessions")
      .select("trainerstunden_session_id");
    if (!error) {
      deskLinkIds = (data || [])
        .map((row) => __asStr(row.trainerstunden_session_id))
        .filter(Boolean);
    }
  } catch (_) {
    deskLinkIds = [];
  }
  const clubIds = new Set();
  for (const session of deskSessions) {
    for (const id of deskPersonIds(session)) clubIds.add(id);
  }
  let clubPeopleById = new Map();
  if (clubIds.size) {
    const loaded = await loadClubPeopleByIds([...clubIds]);
    const rows = loaded?.ok
      ? loaded.data || []
      : Array.isArray(loaded)
        ? loaded
        : [];
    clubPeopleById = new Map(
      (rows || []).filter((p) => p?.id).map((p) => [__asStr(p.id), p]),
    );
  }
  return { deskSessions, deskLinkIds, clubPeopleById, trainers };
}

const NICKNAME_SELECT =
  "id,tenant_id,created_by,person_key,nickname,nickname_norm,created_at,updated_at";

export async function loadWinterDoppelPersonNicknames(tenantId = "") {
  const supabase = await getSupabaseClient();
  if (!supabase) return [];
  let query = supabase
    .from("winterdoppel_person_nicknames")
    .select(NICKNAME_SELECT)
    .order("nickname", { ascending: true });
  const tid = __asStr(tenantId);
  if (tid) query = query.eq("tenant_id", tid);
  const { data, error } = await query;
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function saveWinterDoppelPersonNickname({
  tenantId,
  personKey,
  nickname,
} = {}) {
  const key = __asStr(personKey);
  const nick = __asStr(nickname);
  const tid = __asStr(tenantId);
  if (!key || !nick) {
    throw new Error("nickname-required");
  }
  const supabase = await getSupabaseClient();
  if (!supabase) throw new Error("no-supabase");
  const row = {
    person_key: key,
    nickname: nick,
    nickname_norm: normalizeWinterDoppelPlayerName(nick),
  };
  if (tid) row.tenant_id = tid;
  const { data, error } = await supabase
    .from("winterdoppel_person_nicknames")
    .upsert(row, { onConflict: "tenant_id,nickname_norm" })
    .select(NICKNAME_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteWinterDoppelPersonNickname(id) {
  const nid = __asStr(id);
  if (!nid) return;
  const supabase = await getSupabaseClient();
  if (!supabase) throw new Error("no-supabase");
  const { error } = await supabase
    .from("winterdoppel_person_nicknames")
    .delete()
    .eq("id", nid);
  if (error) throw error;
}

export async function createWinterDoppelOccupancyDeskSession({
  tenantId,
  date,
  startTime,
  durationMin = 90,
  title,
  people = [],
  trainerId,
} = {}) {
  const day = __date(date);
  const start = __asStr(startTime).slice(0, 5);
  if (!day || !/^\d{2}:\d{2}$/.test(start)) {
    throw new Error("occupancy-create-invalid");
  }
  const plannedIds = [];
  let trainer = __asStr(trainerId);
  for (const person of people || []) {
    const keys = occupancyPersonKeys(person);
    const trainerKey = keys.find((key) => key.startsWith("trainer:"));
    if (trainerKey && !trainer) trainer = trainerKey.slice(8);
    const existing =
      __asStr(person.clubPersonId) ||
      (keys.find((key) => key.startsWith("club:")) || "").slice(5);
    if (existing) {
      plannedIds.push(existing);
      continue;
    }
    if (person.kind === "trainer" || person.kind === "both") continue;
    const parsed = analyzePersonNameInput(person.label);
    if (!parsed?.ok) continue;
    const created = await createClubPlayerContactFromParsedName(parsed);
    if (created?.ok && created.data?.id) plannedIds.push(created.data.id);
  }
  const uniqueIds = [...new Set(plannedIds.filter(Boolean))];
  if (!uniqueIds.length && !trainer) {
    throw new Error("occupancy-create-no-people");
  }
  const mins = Math.max(15, Number(durationMin) || 90);
  const payload = {
    id: generateId(12),
    date: day,
    start_time: `${start}:00`.slice(0, 8),
    duration_min: mins,
    trainer_id: trainer || null,
    title: __asStr(title) || "Einzeltermin",
    notes: "Personenplan",
    training_type: uniqueIds.length > 1 ? "group" : "individual",
    group_size: Math.max(1, uniqueIds.length),
    planned_ids: uniqueIds,
    present_ids: [],
    attendance_confirmed: false,
    recurrence: "once",
  };
  const tid = __asStr(tenantId);
  if (tid) payload.tenant_id = tid;
  const result = await createSession(payload);
  if (!result?.ok) {
    throw result?.error || new Error("occupancy-create-failed");
  }
  return result.data;
}
